import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import {
  insertFilesSchema,
  insertFolderSchema,
  insertPurchasesSchema,
  insertQuestionAttemptsSchema,
  insertQuestionOptionsSchema,
  insertQuestionsSchema,
  insertStudySetsSchema,
  insertUsersSchema,
  notes,
  questions,
  studySets,
  userActivityCountsInsertSchema,
  users,
} from "../../schema";

// Extended schemas with additional validation using refinements
export const questionSchema = createInsertSchema(questions, {
  question: (schema) => schema.min(1).max(250),
  answer: (schema) => schema.optional().default(""),
});

export const studySetSchema = createInsertSchema(studySets, {
  isPublic: (schema) => schema.default(true),
  name: (schema) =>
    schema
      .min(1, { error: "Required" })
      .max(50, { error: "Title must be less than 50 characters" }),
});

export const noteSchema = createInsertSchema(notes, {
  name: (schema) =>
    schema
      .min(1, { error: "Required" })
      .max(100, { error: "Name must be less than 100 characters" }),
});

export const userSchema = createInsertSchema(users, {
  email: (schema) =>
    schema.email({ error: "Invalid email" }).min(1, { error: "Required" }),
  firstName: (schema) =>
    schema.min(2, { error: "First name must be at least 2 characters" }),
}).omit({
  id: true,
  university: true,
  onboardedSteps: true,
  aiNotetakerOnboarded: true,
});

export const folderSchema = insertFolderSchema;

export const fileSchema = insertFilesSchema;

export const questionOptionSchema = insertQuestionOptionsSchema;

export const pastedTextSchema = z.object({
  pastedText: z.string().min(200).max(20000),
});

// export const questionArrayFormSchema = z.object({
//   questions: z
//     .array(editQuestionSchema)
//     .min(2, { message: "A minimum of 2 questions are required" }),
// });

// export type QuestionFormValues = z.infer<typeof questionArrayFormSchema>;

export type PlanType = "yearly" | "monthly";

export const incorrectQuestionOptionsZodSchema = z
  .array(z.string().min(1, "Option text cannot be empty"))
  .length(3, "Must have exactly 3 options")
  .describe(
    "An array of incorrect answer options for the multiple-choice question. Must include three options.",
  );

// Create the Zod schema properly
export const SubjectSchema = z.enum([
  "Anatomy",
  "Pharmacology",
  "Accounting",
  "Algebra",
  "Art History",
  "Biology",
  "Chemistry",
  "Education",
  "Geography",
  "Geometry",
  "History",
  "Microbiology",
  "Music Theory",
  "Networking",
  "Nursing",
  "Organic Chemistry",
  "Physics",
  "Psychology",
  "US History",
  "Writing",
  "Statistics",
  "Business",
  "Economics",
  "Computer Science",
  "Sociology",
  "Environmental Science",
  "Calculus",
  "World History",
  "Political Science",
  "Philosophy",
  "Astronomy",
  "Software Development",
  "Health Science",
  "Forensic Science",
  "Finance",
  "Marketing",
  "Entrepreneurship",
  "Human Resources",
  "Supply Chain",
  "Communications",
  "Engineering",
]);

// Define an array of subjects using the .array() method
export const SubjectArray = SubjectSchema.options;

const youtubeUrlRegex =
  /^https?:\/\/(www\.)?youtube\.com\/watch\?v=[\w-]{11}(&\S*)?$|^https?:\/\/(www\.)?youtu\.be\/[\w-]{11}(\?\S*)?$/i;

export const youtubeValidator = z.object({
  url: z.string().url().regex(youtubeUrlRegex, {
    message: "Invalid YouTube video URL",
  }),
});

// Export all insert schemas for direct use
export {
  insertFilesSchema,
  insertFolderSchema,
  insertPurchasesSchema,
  insertQuestionAttemptsSchema,
  insertQuestionOptionsSchema,
  insertQuestionsSchema,
  insertStudySetsSchema,
  insertUsersSchema,
  userActivityCountsInsertSchema,
};
